import { Button, Dialog, DialogContent, DialogTitle, Divider, Stack, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import { stringComparerBy } from "@entrilia/shared/utilities/arrayHelper";
import adminApi from "../../../api/adminApi";
import type { ObjectAccessCategory, ObjectClassDefinition } from "../../../api/types/objectTypes";
import type { AccessValue } from "../../common/AccessEditor";
import AccessEditor from "../../common/AccessEditor";
import MultiselectAutocomplete from "../../common/MultiselectAutocomplete";
import AddContactDialog from "../../pages/fund-structure/common/AddContactDialog";
import type { User, UserAccess } from "./userAccessMatrixModel";
import {
  getCategoryOptions,
  mapObjectContactToUserAccess,
  mapUserAccessToObjectContactRequest,
} from "./userAccessMatrixModel";

interface Props {
  objectId: string;
  objectDefinition: ObjectClassDefinition;
  categories: ObjectAccessCategory[];
  currentAccessItems: UserAccess[];
  allUsers: User[];
  onSave: (updatedAccessItems: UserAccess[]) => void;
  onClose: () => void;
  onUserCreated: (newUser: User) => void;
}

const createObjectContact = withErrorHandling(adminApi.createObjectContact);
const updateObjectContacts = withErrorHandling(adminApi.updateObjectContacts);

const AddContactAccessDialog = ({
  objectId,
  objectDefinition,
  categories,
  currentAccessItems,
  allUsers,
  onSave,
  onClose,
  onUserCreated,
}: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [isSaving, setIsSaving] = useState(false);
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [isCreateContactDialogOpen, setIsCreateContactDialogOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<User[]>([]);

  const [accessValue, setAccessValue] = useState<AccessValue>({
    selectedRole: objectDefinition.supportedContactRoles[0] ?? "None",
    selectedCategoryIds: categories.map((c) => c.id),
  });

  const userOptions = useMemo(() => {
    const currentIds = currentAccessItems.map((c) => c.id);
    return allUsers.filter((c) => !currentIds.includes(c.id)).sort(stringComparerBy((o) => o.name));
  }, [allUsers, currentAccessItems]);

  const { existingContactNames, existingContactEmails } = useMemo(() => {
    const existingContactNames = new Set<string>(allUsers.map((c) => c.name));
    const existingContactEmails = new Set<string>(allUsers.map((c) => c.email.toLowerCase()));
    return { existingContactNames, existingContactEmails };
  }, [allUsers]);

  const categoryOptions = useMemo(() => getCategoryOptions(categories), [categories]);

  const handleContactAdd = async (name: string, email: string) => {
    setIsCreatingContact(true);
    const [contact, error] = await createObjectContact(objectDefinition.objectType, { name, email });
    setIsCreatingContact(false);

    if (error) {
      logError(error, "[AddContactAccessDialog] createObjectContact");
      sendNotificationError(error.message);
      return;
    }

    const newUser = { id: contact.id, name: contact.name, email: contact.email };
    onUserCreated(newUser);
    setSelectedUsers((prev) => [...prev, newUser]);
  };

  const handleSave = async () => {
    const addedAccessItems: UserAccess[] = selectedUsers.map((c) => ({
      id: c.id,
      role: accessValue.selectedRole === "None" ? undefined : accessValue.selectedRole,
      categories: accessValue.selectedCategoryIds,
    }));

    const payload = {
      contacts: [...currentAccessItems, ...addedAccessItems].map(mapUserAccessToObjectContactRequest),
    };

    setIsSaving(true);
    const [updatedContacts, error] = await updateObjectContacts(objectDefinition.objectType, objectId, payload);
    setIsSaving(false);

    if (error) {
      logError(error, "[AddContactAccessDialog] updateObjectContacts");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Contact(s) successfully added to this company");
    onSave(updatedContacts.map(mapObjectContactToUserAccess));
    onClose();
  };

  const isValid = selectedUsers.length > 0;

  return (
    <>
      <Dialog open onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Add New</DialogTitle>
        <DialogCloseButton onClick={onClose} />
        <Divider />

        <DialogContent>
          <Stack spacing={4}>
            <Stack spacing={2}>
              <Typography color="textSecondary">
                Select one or more existing contacts or create multiple new ones.
              </Typography>
              <MultiselectAutocomplete
                options={userOptions}
                values={selectedUsers}
                disabled={isCreatingContact}
                onSelected={(items) => setSelectedUsers(items)}
                getOptionValue={(option) => option.id}
                getOptionLabel={(option) => option.name}
                getOptionDescription={(option) => option.email}
                placeholder="Contacts"
                noItemsText="No contacts found"
                onCreateOption={() => setIsCreateContactDialogOpen(true)}
              />
            </Stack>

            <AccessEditor
              value={accessValue}
              onChange={(update) => setAccessValue((prev) => ({ ...prev, ...update }))}
              roleOptions={["None", ...objectDefinition.supportedContactRoles]}
              categoryOptions={categoryOptions}
            />
          </Stack>
        </DialogContent>

        <Divider />
        <DialogButtons onCancel={onClose}>
          <Button variant="contained" loading={isSaving} onClick={handleSave} disabled={!isValid || isCreatingContact}>
            Add
          </Button>
        </DialogButtons>
      </Dialog>
      {isCreateContactDialogOpen && (
        <AddContactDialog
          mode="add"
          onClose={() => setIsCreateContactDialogOpen(false)}
          onAdd={handleContactAdd}
          existingContactNames={existingContactNames}
          existingContactEmails={existingContactEmails}
        />
      )}
    </>
  );
};

export default AddContactAccessDialog;
